import { useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";
import type { AiSearchResponse, SearchResponse } from "@/api/types";
import { TitleCard } from "@/components/TitleCard";

const EXAMPLES = [
  "a dark science-fiction film with a female lead and a mind-bending ending",
  "something gentle and animated for a seven year old",
  "a funny show about people working night shifts",
];

/**
 * Two search modes side by side.
 *
 * "Describe it" is the AI path: the model turns the sentence into a structured
 * retrieval plan, which is shown to the user. Making the plan visible is the
 * difference between a search box that feels magic and one that feels arbitrary.
 */
export function Search() {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<"keyword" | "ai">("keyword");
  const [results, setResults] = useState<SearchResponse | AiSearchResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "keyword" || query.trim().length < 2) return;
    const handle = setTimeout(async () => {
      try {
        setBusy(true);
        setResults(
          await api.get<SearchResponse>(
            `/api/v1/discovery/search?q=${encodeURIComponent(query)}&mode=hybrid`,
          ),
        );
        setError(null);
      } catch (cause) {
        setError(cause instanceof ApiError ? cause.message : "Search failed");
      } finally {
        setBusy(false);
      }
    }, 260);
    return () => clearTimeout(handle);
  }, [query, mode]);

  async function describeIt(text: string) {
    setQuery(text);
    setMode("ai");
    setBusy(true);
    setError(null);
    try {
      setResults(await api.post<AiSearchResponse>("/api/v1/discovery/ai-search", { query: text }));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "AI search is unavailable");
    } finally {
      setBusy(false);
    }
  }

  const plan = (results as AiSearchResponse | null)?.plan ?? null;

  return (
    <main className="page container">
      <div style={{ paddingTop: 34, maxWidth: 760 }}>
        <h1>Search</h1>
        <div style={{ display: "flex", gap: 8, margin: "18px 0" }}>
          <button
            type="button"
            className={mode === "keyword" ? "btn btn--accent" : "btn btn--ghost"}
            onClick={() => setMode("keyword")}
          >
            Titles, people, genres
          </button>
          <button
            type="button"
            className={mode === "ai" ? "btn btn--accent" : "btn btn--ghost"}
            onClick={() => setMode("ai")}
          >
            Describe it
          </button>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (mode === "ai") void describeIt(query);
          }}
        >
          <div className="field">
            <label htmlFor="q">
              {mode === "ai" ? "Describe what you feel like watching" : "Search the catalog"}
            </label>
            <input
              id="q"
              value={query}
              autoFocus
              placeholder={mode === "ai" ? EXAMPLES[0] : "The Quiet Orbit"}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {mode === "ai" && (
            <button className="btn btn--accent" type="submit" disabled={busy || !query.trim()}>
              {busy ? "Thinking…" : "Find it"}
            </button>
          )}
        </form>

        {mode === "ai" && !results && (
          <div className="stack" style={{ marginTop: 18 }}>
            {EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                className="btn btn--ghost"
                onClick={() => void describeIt(example)}
              >
                {example}
              </button>
            ))}
          </div>
        )}

        {plan && (
          <div className="alert alert--info" style={{ marginTop: 20 }}>
            <strong>Looking for:</strong> {plan.semantic_query}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {plan.kind && <span className="chip">{plan.kind}</span>}
              {plan.genres.map((genre) => (
                <span key={genre} className="chip">
                  {genre}
                </span>
              ))}
              {plan.moods.map((mood) => (
                <span key={mood} className="chip">
                  {mood}
                </span>
              ))}
              {plan.year_from && (
                <span className="chip">
                  {plan.year_from}
                  {plan.year_to && plan.year_to !== plan.year_from ? `–${plan.year_to}` : ""}
                </span>
              )}
            </div>
          </div>
        )}

        {error && <div className="alert alert--error">{error}</div>}
      </div>

      {results && (
        <>
          <p style={{ color: "var(--paper-400)", fontSize: 13 }}>
            {results.count} result{results.count === 1 ? "" : "s"}
          </p>
          <div className="grid">
            {results.results.map((item) => (
              <TitleCard key={item.id} title={item} />
            ))}
          </div>
          {results.count === 0 && (
            <div className="empty">Nothing matched. Try describing it differently.</div>
          )}
        </>
      )}
    </main>
  );
}
